// MasterDashboardChoreCard — Feb 2026 (Randy pt89f).
// States (pending WINS visually over stale done):
//   • empty checkbox: not done
//   • red bordered check: pending parent confirmation (highest priority)
//   • solid green check: confirmed (points awarded)
// Tap on the chore NAME → flips to the parent-written description for
// 30 seconds, then flips back. Tap again to flip immediately.
// pt89f: NO long-press-to-reject (accidentally fired on normal taps).
// Reject is now only via the explicit X button on pending rows.
import { useEffect, useRef, useState } from "react";
import { Check, X } from "@phosphor-icons/react";

const FLIP_MS = 30_000;

export default function MasterDashboardChoreCard({ chore, onTap, onApprove, onReject, compact = false }) {
  const [showDesc, setShowDesc] = useState(false);
  const timerRef = useRef(null);
  useEffect(() => {
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, []);
  const flipToDesc = () => {
    setShowDesc(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setShowDesc(false), FLIP_MS);
  };
  const toggleFlip = () => {
    if (showDesc) { setShowDesc(false); if (timerRef.current) clearTimeout(timerRef.current); }
    else flipToDesc();
  };
  // Pending WINS over done (fixes the stale-done + fresh-pending combo bug).
  const isPending = !!chore.pending;
  const isDone = !isPending && !!chore.done;
  const boxBase = "shrink-0 rounded-md flex items-center justify-center transition-colors";
  const boxSize = compact ? "w-6 h-6" : "w-7 h-7";
  const boxStyle = isPending
    ? "bg-transparent border-2 border-[#E86A5C] text-[#E86A5C]"
    : isDone
      ? "bg-[#7BD389] border border-[#7BD389] text-black"
      : "bg-transparent border border-white/25 text-transparent hover:border-white/60";
  const handleBoxClick = (e) => {
    e.stopPropagation();
    if (isPending) { onApprove?.(); return; }
    onTap?.();
  };
  const rowCls = compact
    ? "flex items-start gap-2 px-3 py-2 rounded-lg bg-white/5 border border-white/8 hover:bg-white/8"
    : "flex items-start gap-3 px-5 py-4 rounded-xl bg-white/5 border border-white/10 hover:bg-white/8";
  return (
    <div className={rowCls} data-testid={`master-dashboard-chore-${chore.event_id}`}>
      <button onClick={handleBoxClick}
        className={`${boxBase} ${boxSize} ${boxStyle}`}
        data-testid={`master-dashboard-chore-box-${chore.event_id}`}
        aria-label={isPending ? "Approve pending" : isDone ? "Completed" : "Mark done"}>
        {(isDone || isPending) && <Check size={compact ? 12 : 14} weight="bold" />}
      </button>
      <button onClick={toggleFlip} className="flex-1 text-left min-w-0" data-testid={`master-dashboard-chore-title-${chore.event_id}`}>
        {!showDesc ? (
          <div className={compact ? "text-sm leading-snug text-white/90" : "text-base leading-snug text-white/90 font-medium"}>
            {chore.title}
          </div>
        ) : (
          <div className={`${compact ? "text-[11px]" : "text-xs"} text-white/60 italic leading-relaxed max-h-24 overflow-y-auto pr-1`}
            data-testid={`master-dashboard-chore-desc-${chore.event_id}`}>
            {chore.notes || "No description yet — ask a parent to add one."}
          </div>
        )}
        {chore.start_time && !showDesc && (
          <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mt-0.5">{chore.start_time}</div>
        )}
      </button>
      {isPending && (
        <button onClick={(e) => { e.stopPropagation(); onReject?.(); }}
          data-testid={`master-dashboard-chore-reject-${chore.event_id}`}
          className="shrink-0 p-1 rounded text-white/30 hover:text-[#E86A5C]"
          title="Reject completion (parent PIN required)">
          <X size={12} />
        </button>
      )}
    </div>
  );
}
